import { LoanType } from './loanApplication';

/**
 * Higher-level program-type groups the Pricing Workbench filter buckets loan types under
 * (Scheme B — Agency buckets Conventional + Conforming together, while FHA/VA/USDA stay
 * distinct chips). Values are stable identifiers, not display labels.
 */
export const ProgramTypeGroup = {
  AGENCY: 'Agency',
  FHA: 'FHA',
  VA: 'VA',
  USDA: 'USDA',
  NON_AGENCY: 'NonAgency',
  HOME_EQUITY: 'HomeEquity',
  OTHER: 'Other',
};

/**
 * Canonical, ordered catalog of every program loan type the platform prices or originates,
 * each tagged with the group it buckets under. The single source of truth behind the loan-type
 * option lists (constants/options) and the program-type filter groupings. Each `value` is the
 * Skynet.Pricing `ProgramLoanType` enum value (mirrored by the AdminPanel `LoanType`), so it
 * matches a priced program's `programLoanType` verbatim.
 */
export const PROGRAM_LOAN_TYPES = [
  { name: 'Conventional', value: LoanType.CONVENTIONAL, group: ProgramTypeGroup.AGENCY },
  { name: 'Conforming', value: LoanType.CONFORMING, group: ProgramTypeGroup.AGENCY },
  { name: 'FHA', value: LoanType.FHA, group: ProgramTypeGroup.FHA },
  { name: 'VA', value: LoanType.VA, group: ProgramTypeGroup.VA },
  { name: 'USDA', value: LoanType.USDA, group: ProgramTypeGroup.USDA },
  { name: 'Jumbo', value: LoanType.JUMBO, group: ProgramTypeGroup.NON_AGENCY },
  { name: 'Non-QM', value: LoanType.NON_QM, group: ProgramTypeGroup.NON_AGENCY },
  { name: 'Non-conforming', value: LoanType.NON_CONFORMING, group: ProgramTypeGroup.NON_AGENCY },
  { name: 'DSCR', value: LoanType.DSCR, group: ProgramTypeGroup.NON_AGENCY },
  { name: 'HELOC', value: LoanType.HELOC, group: ProgramTypeGroup.HOME_EQUITY },
  { name: 'HELoan', value: LoanType.HELOAN, group: ProgramTypeGroup.HOME_EQUITY },
  { name: 'BPL', value: LoanType.BPL, group: ProgramTypeGroup.OTHER },
  { name: 'Personal', value: LoanType.PERSONAL, group: ProgramTypeGroup.OTHER },
  { name: 'Reverse Mortgage', value: LoanType.REVERSE_MORTGAGE, group: ProgramTypeGroup.OTHER },
  { name: 'Chattel', value: LoanType.CHATTEL, group: ProgramTypeGroup.OTHER },
];

/** Filter-chip label per group. */
export const PROGRAM_TYPE_GROUP_LABELS = {
  [ProgramTypeGroup.AGENCY]: 'Agency',
  [ProgramTypeGroup.FHA]: 'FHA',
  [ProgramTypeGroup.VA]: 'VA',
  [ProgramTypeGroup.USDA]: 'USDA',
  [ProgramTypeGroup.NON_AGENCY]: 'Non-Agency',
  [ProgramTypeGroup.HOME_EQUITY]: 'Home Equity',
  [ProgramTypeGroup.OTHER]: 'Other',
};

/** Canonical display order for the program-type group chips. */
export const PROGRAM_TYPE_GROUP_ORDER = [
  ProgramTypeGroup.AGENCY,
  ProgramTypeGroup.FHA,
  ProgramTypeGroup.VA,
  ProgramTypeGroup.USDA,
  ProgramTypeGroup.NON_AGENCY,
  ProgramTypeGroup.HOME_EQUITY,
  ProgramTypeGroup.OTHER,
];

const LOAN_TYPE_TO_GROUP = Object.fromEntries(
  PROGRAM_LOAN_TYPES.map(({ value, group }) => [value, group]),
);

/**
 * The group a raw loan-type value belongs to. An unknown or absent value falls back to Other, so
 * a newly added engine type still surfaces under a chip rather than disappearing from the filter.
 *
 * @param {string} loanType  A ProgramLoanType/LoanType value (e.g. 'Conventional', 'NonQM')
 * @returns {string} A ProgramTypeGroup value
 */
export const getLoanTypeGroup = loanType => LOAN_TYPE_TO_GROUP[loanType] ?? ProgramTypeGroup.OTHER;

/**
 * Builds a `{ name, value }` option list from the canonical catalog, restricted to the given
 * loan-type values (order follows PROGRAM_LOAN_TYPES). Lets every loan-type dropdown derive from
 * one source instead of a hand-maintained parallel array.
 *
 * @param {string[]} values  LoanType values to include
 * @returns {{name: string, value: string}[]}
 */
export const buildLoanTypeOptions = values =>
  PROGRAM_LOAN_TYPES.filter(type => values.includes(type.value)).map(({ name, value }) => ({
    name,
    value,
  }));
